import React, { useContext, useEffect, useMemo } from 'react'
import { Grid, Stack } from '@mui/material'
import { useParams } from 'react-router-dom'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import InventoryCommodityMaterialGrid from 'components/generic/datagrid/InventoryCommodityMaterialGrid'
import InventorySummaryBar from 'components/layouts/InventorySummaryBar'
import { useGetInventoryByCommodityQuery } from 'services/inventory'
import { setDecimalPrecision, parseNumberObject } from 'utils/helpers'
import { transformStrToCapital } from 'utils/router'
import { useSelector } from 'react-redux'

const InventoryCommodityMaterialContainer = () => {
  const { commodityId } = useParams()
  const contentContext = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)
  const { enableFourDecimalPrecisionInInventory } = useSelector(state => state.featureFlags?.variables || {})

  const { data, isLoading } = useGetInventoryByCommodityQuery({ commodityId }, { refetchOnMountOrArgChange: true })

  const { defaultYardUm } = useSelector(state => state.settings)

  const summaryItems = useMemo(
    () => [
      {
        label: 'WIP INVENTORY',
        value: parseNumberObject(data?.wipCost, { format: 'value' }),
        additionalValue: parseNumberObject(data?.wipWeight, { format: 'weight', unitOverride: defaultYardUm }),
      },
      {
        label: 'FG INVENTORY',
        value: parseNumberObject(data?.finishedGoodCost, { format: 'value' }),
        additionalValue: parseNumberObject(data?.finishedGoodWeight, { format: 'weight', unitOverride: defaultYardUm }),
      },
      {
        label: 'TOTAL INVENTORY',
        value: `$${setDecimalPrecision(parseNumberObject(data?.wipCost) + parseNumberObject(data?.finishedGoodCost), {
          decimalPrecisionOverride: 2,
        })}`,
        additionalValue: `${setDecimalPrecision(
          parseNumberObject(data?.wipWeight) + parseNumberObject(data?.finishedGoodWeight),
          {
            decimalPrecisionOverride: 1,
            removeTrailingZeros: true,
          },
        )} ${defaultYardUm}`,
      },
    ],
    [data],
  )

  useEffect(() => {
    contentContext.setContainerTitle(transformStrToCapital(data?.commodity?.name?.toLowerCase() || commodityId))
    tabsContext.setContainerTabsVisibility(false)
  }, [data])

  const transformedRows = (data?.Items ?? []).map(material => ({
    ...material,
    id: material?.materialTypeId,
    name: material?.materialTypeName,
    wip: parseNumberObject(material?.wip),
    wipFormatted: parseNumberObject(material?.wip, { format: 'weight', unitOverride: defaultYardUm }),
    finishedGoods: parseNumberObject(material?.finishedGoods),
    finishedGoodsFormatted: parseNumberObject(material?.finishedGoods, {
      format: 'weight',
      unitOverride: defaultYardUm,
    }),
    averageCost: parseNumberObject(material?.averageCost),
    averageCostFormatted: parseNumberObject(material?.averageCost, {
      format: 'price',
      enableFourDecimalPrecisionInInventory,
      unitOverride: defaultYardUm,
    }),
    value: parseNumberObject(material?.value),
    valueFormatted: parseNumberObject(material?.value, { format: 'value' }),
  }))

  return (
    <Stack>
      <InventorySummaryBar items={summaryItems} />
      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <InventoryCommodityMaterialGrid rows={transformedRows} loading={isLoading} />
      </Grid>
    </Stack>
  )
}

export default InventoryCommodityMaterialContainer
